import { CircularProgress, Snackbar } from '@material-ui/core'
import ButtonShadow from 'components/ButtonShadow/ButtonShadow'
import React, { useCallback, useEffect, useState } from 'react'
import { Colors } from 'styles'
import * as Styled from './SpotifyIntegrationCard.styled'
import { Container } from './SpotifyIntegrationCard.styled'
import {
  createSpotifyPartner,
  deactivateSpotifyPartner,
  getSpotifyPartner,
} from 'services/api'
import { SpotifyPartnerDTO } from 'types'
import Dialog from 'components/Dialog/Dialog'
import SpotifyIntegrationStatus from 'components/SpotifyIntegrationStatus/SpotifyIntegrationStatus'
import LinkWrapper from 'components/LinkWrapper/LinkWrapper'

interface IError {
  message?: string
  tryAgainFunctionName?: string
}

interface SpotifyIntegrationCardProps {
  podcastId: string
}

const SpotifyIntegrationCard: React.FC<SpotifyIntegrationCardProps> = ({
  podcastId,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<IError>({})
  const [isActive, setIsActive] = useState<boolean>(false)
  const [spotifyPartner, setSpotifyPartner] =
    useState<SpotifyPartnerDTO | null>(null)
  const [snackbarText, setSnackbarText] = useState<string | null>(null)
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false)

  const fetchSpotifyPartner = useCallback(async () => {
    setIsLoading(true)
    setError({})
    const spotifyPartnerOrError = await getSpotifyPartner(podcastId)

    if (spotifyPartnerOrError.hasFailed()) {
      setIsLoading(false)

      if (
        spotifyPartnerOrError.error.message ===
        'Spotify partner does not exists'
      ) {
        setIsActive(false)
        return
      }

      setError({
        message: 'Falha ao buscar seus dados',
        tryAgainFunctionName: 'fetchSpotifyPartner',
      })
      return
    }

    setIsLoading(false)
    setIsActive(spotifyPartnerOrError.data.isActive)
    setSpotifyPartner(spotifyPartnerOrError.data)
  }, [podcastId])

  useEffect(() => {
    fetchSpotifyPartner()
  }, [fetchSpotifyPartner])

  const handleCreateSpotifyPartner = useCallback(async () => {
    setIsLoading(true)
    const createSpotifyPartnerResult = await createSpotifyPartner(podcastId)

    if (createSpotifyPartnerResult.hasFailed()) {
      setSnackbarText('Falha ao ativar integração com o Spotify')
    }

    fetchSpotifyPartner()
  }, [podcastId, fetchSpotifyPartner])

  const handleDeactivateSpotifyPartner = useCallback(async () => {
    setIsDeactivateModalOpen(false)
    setIsLoading(true)
    const createSpotifyPartnerResult = await deactivateSpotifyPartner(podcastId)

    if (createSpotifyPartnerResult.hasFailed()) {
      setSnackbarText('Falha ao desativar integração com o Spotify')
    }

    fetchSpotifyPartner()
  }, [podcastId, fetchSpotifyPartner])

  const handleToggle = useCallback(
    async (isActive: boolean) => {
      if (isActive) {
        handleCreateSpotifyPartner()
        return
      }

      setIsDeactivateModalOpen(true)
    },
    [handleCreateSpotifyPartner],
  )

  const getShowUrl = () => {
    if (!spotifyPartner?.showUri) return ''
    const showId = spotifyPartner.showUri.split(':')[2]
    return `https://open.spotify.com/show/${showId}`
  }

  if (isLoading) {
    return (
      <Container>
        <Styled.InfoContainer>
          <CircularProgress style={{ color: Colors.PURPLE[400] }} />
        </Styled.InfoContainer>
      </Container>
    )
  }

  if (error.message) {
    return (
      <Container>
        <Styled.InfoContainer>
          <Styled.InfoText>{error.message}</Styled.InfoText>
          <ButtonShadow
            label="Tentar novamente"
            variant="secondary"
            onPress={
              error.tryAgainFunctionName === 'fetchSpotifyPartner'
                ? fetchSpotifyPartner
                : undefined
            }
          />
        </Styled.InfoContainer>
      </Container>
    )
  }

  return (
    <Container>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={!!snackbarText}
        onClose={() => setSnackbarText(null)}
        message={snackbarText}
        autoHideDuration={5000}
      />

      <Dialog
        dialogText={
          <Styled.DeactivateModalContainer>
            <Styled.DeactivateModalText>
              Tem certeza que deseja desativar a conexão com o Spotify?
            </Styled.DeactivateModalText>
            <Styled.DeactivateModalText>
              Seus episódios exclusivos não estarão mais disponíveis diretamente
              no Spotify para seus apoiadores.
            </Styled.DeactivateModalText>

            <Styled.DeactivateModalActions>
              <ButtonShadow
                label="Cancelar"
                variant="secondary"
                onPress={() => setIsDeactivateModalOpen(false)}
              />
              <ButtonShadow
                label="Desativar"
                variant="primary"
                onPress={handleDeactivateSpotifyPartner}
              />
            </Styled.DeactivateModalActions>
          </Styled.DeactivateModalContainer>
        }
        handleCloseDialog={() => setIsDeactivateModalOpen(false)}
        isDialogOpen={isDeactivateModalOpen}
      />

      <Styled.HeaderContainer>
        <div>
          <Styled.SpotifyLogo />
        </div>
        <Styled.TextContainer>
          <Styled.Title>Spotify</Styled.Title>
          <Styled.Description>
            Permita que seus apoiadores ouçam episódios exclusivos diretamente
            no spotify.{' '}
            <Styled.AboutLink
              href="https://orelohelp.zendesk.com/hc/pt-br"
              target="_blank"
              rel="noopener noreferrer"
            >
              saiba mais
            </Styled.AboutLink>
          </Styled.Description>
        </Styled.TextContainer>
        <div>
          <Styled.StyledSwitch
            checked={isActive}
            onChange={(_, isActive) => handleToggle(isActive)}
            color="default"
          />
        </div>
      </Styled.HeaderContainer>

      {spotifyPartner?.isActive && (
        <>
          <Styled.Divider />
          <Styled.ContentContainer>
            <SpotifyIntegrationStatus status={spotifyPartner.status} />
            <LinkWrapper url={getShowUrl()} />
          </Styled.ContentContainer>
        </>
      )}
    </Container>
  )
}

export default SpotifyIntegrationCard
